import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Descriptions, Button, Tag, Spin, message } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import Axios from "../../../services/AxiosInterceptor";

const ViewBookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookingDetails();
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(`/api/booking/${id}`);
      setBooking(response.data);
    } catch (error) {
      message.error("Failed to load booking details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h3>Booking not found</h3>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Action Buttons - Hidden on Print */}
      <div className="no-print" style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          Print Receipt
        </Button>
      </div>

      {/* Receipt Card */}
      <Card
        title={
          <div style={{ textAlign: "center" }}>
            <h2 style={{ margin: 0, color: "#1890ff" }}>BOOKING RECEIPT</h2>
            <p style={{ margin: "5px 0", fontSize: "14px", color: "#666" }}>
              YogaMatic - Booking Confirmation
            </p>
          </div>
        }
        bordered
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        {/* Booking ID and Status */}
        <div style={{ marginBottom: "20px", textAlign: "center" }}>
          <Tag color="blue" style={{ fontSize: "14px", padding: "5px 15px" }}>
            Booking ID: {booking._id}
          </Tag>
          <Tag
            color={booking.paymentStatus === "CONFIRMED" ? "green" : "orange"}
            style={{ fontSize: "14px", padding: "5px 15px", marginLeft: "10px" }}
          >
            {booking.paymentStatus}
          </Tag>
        </div>

        {/* Customer Information */}
        <Descriptions
          title="Customer Information"
          bordered
          column={2}
          size="small"
          style={{ marginBottom: "20px" }}
        >
          <Descriptions.Item label="Customer ID" span={2}>
            {booking.userId}
          </Descriptions.Item>
          <Descriptions.Item label="Name">{booking.username}</Descriptions.Item>
          <Descriptions.Item label="Email">{booking.email}</Descriptions.Item>
          <Descriptions.Item label="Mobile">{booking.mobile}</Descriptions.Item>
        </Descriptions>

        {/* Service Information */}
        <Descriptions
          title="Service Information"
          bordered
          column={2}
          size="small"
          style={{ marginBottom: "20px" }}
        >
          <Descriptions.Item label="Service ID" span={2}>
            {booking.serviceId}
          </Descriptions.Item>
          <Descriptions.Item label="Service Name" span={2}>
            {booking.serviceName}
          </Descriptions.Item>
          <Descriptions.Item label="Location">{booking.location}</Descriptions.Item>
          <Descriptions.Item label="Resource">{booking.resource}</Descriptions.Item>
          <Descriptions.Item label="Date">{booking.date}</Descriptions.Item>
          <Descriptions.Item label="Time">{booking.time}</Descriptions.Item>
          <Descriptions.Item label="Seats Booked">{booking.seatsBooked}</Descriptions.Item>
          <Descriptions.Item label="Service Status">
            <Tag color={booking.serviceStatus === "Completed" ? "green" : "blue"}>
              {booking.serviceStatus}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Subscription Details (if applicable) */}
        {booking.is_subscription && (
          <Descriptions
            title="Subscription Details"
            bordered
            column={2}
            size="small"
            style={{ marginBottom: "20px" }}
          >
            <Descriptions.Item label="Subscription Type" span={2}>
              <Tag color="purple">SUBSCRIPTION</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {booking.subscriptionStart}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {booking.subscriptionEnd}
            </Descriptions.Item>
            {booking.subscriptionConfig && (
              <Descriptions.Item label="Duration" span={2}>
                {booking.subscriptionConfig.years > 0 && `${booking.subscriptionConfig.years} Years `}
                {booking.subscriptionConfig.months > 0 && `${booking.subscriptionConfig.months} Months `}
                {booking.subscriptionConfig.days > 0 && `${booking.subscriptionConfig.days} Days`}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {/* Payment Information */}
        <Descriptions
          title="Payment Information"
          bordered
          column={2}
          size="small"
          style={{ marginBottom: "20px" }}
        >
          <Descriptions.Item label="Amount Paid" span={2}>
            <strong style={{ fontSize: "18px", color: "#52c41a" }}>
              ₹{booking.amountPaid}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Order ID">{booking.orderId}</Descriptions.Item>
          <Descriptions.Item label="Transaction ID">
            {booking.transactionId}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Status" span={2}>
            <Tag color={booking.paymentStatus === "CONFIRMED" ? "green" : "orange"}>
              {booking.paymentStatus}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Rating Status */}
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Rating Status">
            <Tag color={booking.ratingStatus ? "green" : "default"}>
              {booking.ratingStatus ? "Rated" : "Not Rated"}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Footer */}
        <div style={{ marginTop: "30px", textAlign: "center", color: "#999", fontSize: "12px" }}>
          <p>Thank you for choosing YogaMatic!</p>
          <p>For any queries, please contact our support team.</p>
        </div>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewBookingDetails;